import omit from '@tinkoff/utils/object/omit'
import { useFormAPI } from 'api/form'
import { logger } from 'lib/logger'
import {
  isEmail,
  isPhone,
  notEmpty,
  notEquals,
  validate,
  ValidationErrors,
  ValidationRules
} from 'lib/validation/validataion'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useFormConfig } from 'stages/form'
import styled from 'styled-components'
import { Button } from 'ui/button'
import { Icon } from 'ui/icon'

import { getProjectName } from '../../../lib/get-project-name'
import { getFromStorage, setToStorage } from '../../../lib/storage'
import { createMediaStyles } from '../../../lib/styled-helpers'
import { FormState, GenericField, GroupDeclaration } from '../types'
import { Group } from './form-group'

const createDefaultState = (groups: GroupDeclaration[]): FormState => {
  return groups.reduce((acc, group) => {
    const tempState = (group.fields as GenericField[]).reduce((innerAccum: FormState, field) => {
      return innerAccum[field.name]
        ? innerAccum
        : { ...innerAccum, [field.name]: group.type === 'checkbox' ? false : field.value }
    }, {})
    return { ...acc, ...tempState }
  }, {})
}

const createValidationRules = (
  groups: GroupDeclaration[],
  defaultFieldValidationErrorMessage: string,
  defaultFieldRequiredErrorMessage: string
): ValidationRules => {
  const validationRules: ValidationRules = {}

  groups.forEach((group) => {
    group.fields.forEach((field) => {
      const fieldValidationRules = []
      const validationErrorMessage = field.validationErrorMessage || defaultFieldValidationErrorMessage
      const requiredErrorMessage = field.requiredErrorMessage || defaultFieldRequiredErrorMessage

      if (group.type !== 'hidden' && (field as GenericField).required) {
        fieldValidationRules.push({
          validator: group.type === 'checkbox' ? notEquals(false) : notEmpty,
          error: requiredErrorMessage
        })
      }

      if (field.name === 'email')
        fieldValidationRules.push({
          validator: isEmail,
          error: validationErrorMessage
        })

      if (field.name === 'phone')
        fieldValidationRules.push({
          validator: isPhone,
          error: validationErrorMessage
        })

      validationRules[field.name] = fieldValidationRules
    })
  })

  return validationRules
}

export const Form: FC<{
  skipRender?: boolean
  formDataPayload?: any | null
  sent?: boolean
  setSent?: any
  idCar?: string
  creditDiscount?: number
  tradeInDiscount?: number
  insuranceDiscount?: number
  textPosition?: string
}> = ({
  skipRender = false,
  formDataPayload,
  sent,
  setSent,
  idCar,
  creditDiscount,
  tradeInDiscount,
  insuranceDiscount,
  textPosition
}) => {
  let { groups, action, title = '', subtitle = '', id, i18n, titleTag } = useFormConfig()

  const [state, setState] = useState(createDefaultState(groups))
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [isSuccess, setIsSuccess] = useState<boolean>(false)

  useEffect(() => {
    if (getProjectName() === 'avito') {
      setState((prevState) => {
        if (getFromStorage('personalData')) {
          prevState.name = getFromStorage('personalData').name
          prevState.processing_of_personal_data = getFromStorage('personalData').processing_of_personal_data
          prevState.phone = getFromStorage('personalData').phone
        }
        return prevState
      })
    }
  }, [sent])

  const validationRules = useMemo(
    () => createValidationRules(groups, i18n.defaultFieldValidationErrorMessage, i18n.defaultFieldRequiredErrorMessage),
    []
  )

  const setGroupState = (fieldName: string, value: boolean | string) => {
    setState({ ...state, [fieldName]: value })
  }
  const onFieldValidateRequest = (fieldName: string | undefined) => {
    setErrors(validateState(fieldName))
  }
  const validateState = (fieldName?: string): ValidationErrors => {
    if (!fieldName) return validate(state, validationRules)

    const newErrors = validate(state, validationRules)

    if (newErrors[fieldName]) {
      return { ...errors, [fieldName]: newErrors[fieldName] }
    } else {
      return omit([fieldName], errors)
    }
  }
  const { loading, isError, post } = useFormAPI()

  const submitHandler = (event: React.FormEvent<HTMLButtonElement>) => {
    event.preventDefault()
    const errors = validateState()
    setErrors(errors)
    document.dispatchEvent(new CustomEvent('kodix:ecom:sendClicked', { detail: { state: state } }))

    const formatData = (data: any) => {
      let ArrData = Object.entries(data).map((value) => {
        value[1] = { value: value[1] }
        return value
      })
      return Object.fromEntries(ArrData)
    }

    if (Object.keys(errors).length === 0) {
      let dataForm = state
      if (getProjectName() === 'avito') {
        dataForm = appendToFormData(state, formDataPayload)
        /*Берем скидки из localStorage*/
        let discountSettings = getFromStorage('discountSettings')
        if (discountSettings && idCar && discountSettings[idCar]) {
          dataForm = appendToFormData(dataForm, {
            credit_discount: discountSettings[idCar].creditDiscount ? creditDiscount : 0,
            trade_in_discount: discountSettings[idCar].tradeInDiscount ? tradeInDiscount : 0,
            insurance_discount: discountSettings[idCar].insuranceDiscount ? insuranceDiscount : 0
          })
        }
      }
      post(action, dataForm)
        .then((response) => {
          if (response && response.status !== 200) {
            logger.log('[Form/ResponseError]: ', response)
          }
          document.dispatchEvent(new CustomEvent('kodix:ecom:formSent', { detail: { state: state } }))
          document.dispatchEvent(
            new CustomEvent('@kodix/one/form-success', {
              detail: {
                data: {
                  fields: formatData(state)
                },
                meta: {
                  action: action,
                  errorMessage: null,
                  id: 'avn_request',
                  method: 'post',
                  status: 'success'
                }
              }
            })
          )
          setIsSuccess(true)
          if (getProjectName() === 'avito') {
            setToStorage('personalData', state)
          }
        })
        .catch(() => setIsSuccess(false))
        .finally(() => setSent(true))
    }
  }

  if (skipRender && getProjectName() !== 'avito') return <div id={id} className={'avn-form'} />

  const getNumberTagH = (str: string) => {
    return Number(str.slice(1))
  }
  const numberTagH = titleTag && getNumberTagH(titleTag)

  return (
    <Wrapper>
      {sent && !loading && (
        <>
          <IconWrapper isSuccess={isSuccess}>
            <Icon name={isError ? 'cancel' : 'check'} size={1.2} />
          </IconWrapper>
          <InformMessage>
            {!isError ? (
              <div>
                {i18n.successMessage}
                <SubSuccessMessage>{i18n.subSuccessMessage}</SubSuccessMessage>
              </div>
            ) : (
              i18n.errorMessage
            )}
          </InformMessage>
        </>
      )}
      {!sent && (
        <>
          <Head>
            {title && (
              <Title
                textPosition={textPosition}
                as={
                  numberTagH === 1
                    ? 'h1'
                    : numberTagH === 2
                    ? 'h2'
                    : numberTagH === 3
                    ? 'h3'
                    : numberTagH === 4
                    ? 'h4'
                    : numberTagH === 5
                    ? 'h5'
                    : numberTagH === 6
                    ? 'h6'
                    : 'h1'
                }
              >
                {getProjectName() === 'avito' && formDataPayload && formDataPayload.title
                  ? formDataPayload.title
                  : title}
              </Title>
            )}
            {subtitle && <Subtitle textPosition={textPosition}>{subtitle}</Subtitle>}
          </Head>
          <InnerForm id={id}>
            <Wrap>
              {groups.map((group, index) => {
                return (
                  <Group
                    {...group}
                    value={state}
                    onFieldChange={setGroupState}
                    key={group.type + index}
                    disabled={loading}
                    onNeedFieldValidation={onFieldValidateRequest}
                    errors={errors}
                  />
                )
              })}
            </Wrap>
            <ButtonWrapper>
              <Button
                block={getProjectName() === 'avito' ? false : true}
                size={'medium'}
                onClick={(event) => {
                  return submitHandler(event)
                }}
                disabled={loading}
              >
                {i18n.buttonText}
              </Button>
            </ButtonWrapper>
          </InnerForm>
        </>
      )}
    </Wrapper>
  )
}

const appendToFormData = (state: object, payload?: object | null) => {
  return Object.assign({}, state, payload)
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${() => getProjectName() === 'avito' && 'height: 100%;'}
`

const Head = styled.div`
  margin-bottom: ${({ theme }) => (getProjectName() === 'avito' ? 0 : theme.spacing[10])};
  text-align: center;
`

const Title = styled.h1<{ textPosition?: string }>`
  margin-top: 0;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  ${({ theme }) => (getProjectName() === 'avito' ? 'font-size: 24px' : theme.textStyles.h1)};
  color: ${({ theme }) =>
    getProjectName() === 'skywell' ? theme.palette.brand.secondaryLight : theme.palette.grey[900]};
  text-transform: ${() => (getProjectName() === 'avito' ? 'none' : 'uppercase')};
  line-height: ${() => getProjectName() === 'avito' && '32px'};
  text-align: ${({ textPosition }) => {
    return textPosition ? textPosition : 'center'
  }};
  ${createMediaStyles((textPosition) => ({
    phone: textPosition ? `text-align:${textPosition}` : `text-align: left`,
    tablet: textPosition ? `text-align: ${textPosition}` : 'text-align: center'
  }))}
`

const Subtitle = styled.h3<{ textPosition?: string }>`
  margin-top: 0;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  ${({ theme }) => (getProjectName() === 'avito' ? 'font-size: 14px' : theme.textStyles.h3)};
  color: ${({ theme }) => theme.palette.grey[700]};
  font-weight: ${() => getProjectName() === 'avito' && 'normal'};
  text-align: ${({ textPosition }) => textPosition};
`

const IconWrapper = styled.div<{ isSuccess: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.spacing[14]};
  height: ${({ theme }) => theme.spacing[14]};
  margin: ${({ theme }) => `${theme.spacing[4]} auto`};
  border-radius: 50%;
  background-color: ${({ theme, isSuccess }) =>
    getProjectName() === 'avito' && isSuccess ? '#04E061' : theme.palette.grey[500]};
  color: ${({ theme }) => (getProjectName() === 'avito' ? 'white' : theme.palette.brand.primary)};
`

const InformMessage = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.palette.grey[800]};
  font-weight: 300;
  ${({ theme }) => theme.textStyles.p1};
  ${() => {
    if (getProjectName() === 'avito') {
      return 'font-weight: 700;   margin: 0 0 4rem;'
    } else return false
  }};
  ${createMediaStyles(() => ({
    phone: `${getProjectName() === 'avito' && 'fon-size: 1.6rem'}`,
    tablet: `${getProjectName() === 'avito' && 'font-size: 3.2rem; padding: 0 8rem; line-height: 36px;'}`
  }))}
`

const InnerForm = styled.form`
  ${() => getProjectName() === 'avito' && 'flex-grow: 1;'}
`

const Wrap = styled.div`
  ${createMediaStyles(() => ({
    phone: `${getProjectName() === 'avito' ? 'height:auto' : 'height:calc(100vh - 3.2rem)'}`,
    tablet: 'height: auto'
  }))}
`

const ButtonWrapper = styled.div`
  max-width: ${({ theme }) => (getProjectName() === 'avito' ? 'none' : theme.spacing[72])};
  margin: ${({ theme }) => `${theme.spacing[12]} auto ${theme.spacing[0]}`};
  margin: ${({ theme }) => getProjectName() === 'avito' && `${theme.spacing[12]} auto ${theme.spacing[0]} 0`};
`

const SubSuccessMessage = styled.div`
  font-weight: normal;
  font-size: 1.6rem;
`
